<template>
  <div class="e-time-picker">
    <e-input
      v-model="value"
      ref="inputEl"
      :label="label"
      :maska="timeMask"
      :placeholder="timeMask"
      :rules="rules"
      :error-messages="errorMessages"
      @update:model-value="onInput"
      @click:clear="submit(false)"
      @blur="onMenuToggle"
    >
      <template #append-inner>
        <e-icon name="clock" />
      </template>
      <template v-if="slots.label" #label>
        <slot name="label" />
      </template>
    </e-input>
    <v-menu
      v-model="isOpened"
      :close-on-content-click="false"
      content-class="e-time-picker__menu-content"
      activator="parent"
      @update:modelValue="onMenuToggle"
    >
      <div class="e-time-picker__menu">
        <div class="e-time-picker__menu-cols">
          <div class="e-time-picker__col">
            <e-btn
              v-for="i in 24"
              :key="i"
              icon
              size="S"
              :variant="btnHoursVariant(i - 1)"
              class="e-time-picker__btn"
              @click="selectHours(i - 1)"
            >
              {{ i - 1 }}
            </e-btn>
          </div>
          <div class="e-time-picker__col">
            <e-btn
              v-for="i in 6"
              :key="i"
              icon
              size="S"
              :variant="btnMinutesVariant(i)"
              class="e-time-picker__btn"
              @click="selectMinutes(i)"
            >
              {{ `${i - 1}0` }}
            </e-btn>
          </div>
        </div>
      </div>
    </v-menu>
  </div>
</template>

<script setup lang="ts">
import { ref, toRef, watch } from 'vue';
import type { InputRules } from '@/core/types/components/components';
import type { IInput } from '@/core/types/components/input';
import { EBtn, EIcon, EInput } from '@/core/components/ui';
import { timeMask } from '@/core/consts/masks';
import { getPTHTime, getTimeFromPTH } from '@/core/utils/date';
import { replaceStringAt } from '@/core/utils/string';
import { timeRule } from '@/core/utils/validation';

const props = defineProps<{
  modelValue: string | null;
  label?: string;
  rules?: InputRules;
  errorMessages?: string | string[];
}>();

const slots = defineSlots<{
  label?: (props: unknown) => void;
}>();

const emit = defineEmits<{
  (event: 'update:modelValue', value: string | null): void;
}>();

const value = ref<string | null>(null);

const activeHours = toRef(() => value.value?.substring(0, 2));
const activeMinutes = toRef(() => value.value?.substring(3, 5));

watch(
  () => props.modelValue,
  () => {
    if (props.modelValue) {
      value.value = getTimeFromPTH(props.modelValue);
    }
  },
  {
    immediate: true,
  },
);

const isOpened = ref(false);

const rules = toRef(() => {
  let r = [timeRule()] as InputRules;

  if (props.rules) {
    r = [...r, ...props.rules];
  }

  return r;
});

const submit = (close = true) => {
  if (value.value) {
    const needSigns = 5 - value.value.length;

    if (needSigns) {
      value.value = replaceStringAt(`${value.value}${'0'.repeat(needSigns)}`, 2, ':');
    }
  }

  emit('update:modelValue', value.value ? getPTHTime(value.value) : null);

  if (close) {
    isOpened.value = false;
  }
};

const onInput = () => {
  if (value.value?.length === 2) {
    value.value += ':';

    setTimeout(() => {
      if (inputEl.value) {
        const input = inputEl.value.input.$el.querySelector('input');
        if (input) {
          input.selectionStart = input.selectionEnd = input.value.length;
        }
      }
    });
  }
};

const onMenuToggle = () => {
  if (!isOpened.value) {
    submit();
  }
};

const inputEl = ref<IInput>();

const btnHoursVariant = (i: number) => {
  let x = String(i);

  if (x.length < 2) {
    x = `0${x}`;
  }

  return x === activeHours.value ? 'tonal' : 'ghost';
};

const btnMinutesVariant = (i: number) => (`${i - 1}0` === activeMinutes.value ? 'tonal' : 'ghost');

const selectHours = (hours: number) => {
  let newHours = String(hours);

  if (newHours.length < 2) {
    newHours = `0${hours}`;
  }

  value.value = replaceStringAt(value.value || '', 0, newHours);

  inputEl.value?.focus();
};

const selectMinutes = (i: number) => {
  const v = value.value && value.value.length >= 2 ? value.value : '00:';

  const minutes = `${i - 1}0`;

  value.value = replaceStringAt(v, 3, minutes);

  submit();
};
</script>

<style scoped lang="scss">
.e-time-picker {
  width: 100%;

  &__menu {
    display: flex;
    flex-direction: column;

    max-height: 340px;
    padding: 16px;

    background-color: $main-bg-color;
    border-radius: $border-radius-l;
    box-shadow: $box-shadow;

    &-cols {
      display: flex;

      max-height: 256px;

      gap: 16px;
    }
  }

  &__col {
    display: flex;
    flex-direction: column;

    gap: 8px;

    width: 100%;
    overflow: auto;
  }

  &__btn {
    width: 100%;
  }
}

:deep(.e-time-picker__menu-content) {
  &[style*='--v-overlay-anchor-origin: top left'] {
    transform: translateY(-10px);
  }
}
</style>
